import { FC, PropsWithChildren, useState } from 'react'
import CropImage from 'components/CropImage'
import { Button } from '@wellesley/ui'
import { UploadFileProps } from 'components/ChangeImgButton'
import CommonDialog from 'components/modals/CommonDialog'
import { Sizes } from '../../types'
import objectUrlToFile from '../../helpers/objectUrlToFile'
import UploadButton from '../UploadButton'
import useImgMaxHeight from '../../hooks/useImgMaxHeight'
import ZoomControl from './ZoomControl'
import useUnsavedChangesModal from 'hooks/useUnsavedChangesModal'

type CmpProps = {
  open: boolean
  sizes: Sizes
  aspect: number
  title?: string
  accept?: string
  preview?: string
  titleIcon?: React.ReactNode
  circularCrop?: boolean
  onClose: () => void
  onSave: (props: UploadFileProps[]) => void
  onChange: (props: UploadFileProps[]) => void
}

const UploadMediaModal: FC<PropsWithChildren<CmpProps>> = ({
  open,
  sizes,
  aspect,
  children,
  circularCrop,
  preview,
  onSave,
  onClose,
  onChange,
  title = 'Drag or use arrow keys to reposition image',
  titleIcon,
  accept,
}) => {
  const [croppedBlobUrl, setCroppedBlobUrl] = useState<string | null>(null)
  const [zoom, setZoom] = useState<number>(1)
  const [minZoom, setMinZoom] = useState<number>(1)

  const resetAndClose = () => {
    resetZoom()
    onClose()
  }

  const { renderConfirmModal, triggerModal } = useUnsavedChangesModal({
    onDiscard: resetAndClose,
  })

  const resetZoom = () => {
    setZoom(1)
    setMinZoom(1)
  }

  const handleSave = async () => {
    if (!croppedBlobUrl) return

    const file = await objectUrlToFile(croppedBlobUrl, 'cropped-image.jpg')

    onSave([{ file, objectUrl: croppedBlobUrl }])
    resetAndClose()
  }

  const onCancelCrop = triggerModal

  const [ref, imgMaxHeight] = useImgMaxHeight()

  return (
    <>
      {renderConfirmModal()}
      <CommonDialog
        open={open}
        title={title}
        titleIcon={titleIcon}
        childrenClassName="!p-0"
        onCloseModal={triggerModal}
        data-testid="upload_media_modal"
        className="max-w-[600px]"
      >
        <div ref={ref} className="p-m">
          {preview && (
            <div className="grow text-center">
              <CropImage
                src={preview}
                sizes={sizes}
                aspect={aspect}
                circularCrop={circularCrop}
                imgMaxHeight={imgMaxHeight}
                onChange={setCroppedBlobUrl}
                zoom={zoom}
                onMinZoomChange={(z) => {
                  setMinZoom(Math.max(z, 1))
                }}
              />
            </div>
          )}
        </div>
        <ZoomControl zoom={zoom} setZoom={setZoom} min={minZoom} />
        <div className="gap-xxs p-m flex items-center justify-end border-t border-gray-200">
          {children && accept ? (
            <Button
              as="label"
              variant="secondary"
              size="m"
              className="cursor-pointer"
            >
              {children}
              <UploadButton accept={accept} onChange={onChange} />
            </Button>
          ) : (
            <Button variant="secondary" onClick={onCancelCrop}>
              Cancel
            </Button>
          )}
          <Button onClick={() => void handleSave()}>Save</Button>
        </div>
      </CommonDialog>
    </>
  )
}

export default UploadMediaModal
